import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getClazz,
  getSubClazz,
  getPortOfExportsAndFactoryCities,
  getAllCountries,
  getMerchTypes,
  getItemTypes,
  getSizesByItemTypeId,
  getMerchStyles,
  getVMMAttributes,
  getDynamicMasterApexIds,
  getStandardApexIds,
  getAutocompleteAttributeValues,
  getDeptStandardReturnPolicies,
  getColorFamilies,
  getPatternFamilies,
  getPids,
  getPidsDetails,
} from '../../services/attribute-values-service'
import { getVendorsByBpIds } from '../../services/events-service'
import { RootState } from 'store/store'
import { getDynamicMasterMap } from '../../services/attribute-values-service'

export const fetchClazzAction = createAsyncThunk(
  'attributeValues/fetchClazz',
  async (deptId: any) => {
    return await getClazz(deptId)
  },
)

export const fetchSubClazzAction = createAsyncThunk(
  'attributeValues/fetchSubClazz',
  async (deptClazz: any) => {
    return await getSubClazz(deptClazz)
  },
)

export const fetchPortOfExportsAndFactoryCitiesAction = createAsyncThunk(
  'attributeValues/fetchPortOfExportsAndFactoryCities',
  async (countryOfOrigins: any) => {
    return await getPortOfExportsAndFactoryCities(countryOfOrigins)
  },
)

export const fetchAllCountriesAction = createAsyncThunk(
  'attributeValues/fetchCountries',
  async () => {
    return await getAllCountries()
  },
)

export const fetchMerchTypesAction = createAsyncThunk(
  'attributeValues/fetchMerchTypes',
  async (data: { deptIds; merchTypeIds? }) => {
    const { deptIds, merchTypeIds } = data
    return await getMerchTypes(deptIds, merchTypeIds)
  },
)

export const fetchItemTypesAction = createAsyncThunk(
  'attributeValues/fetchItemTypes',
  async (merchTypeId: any) => {
    return await getItemTypes(merchTypeId)
  },
)

export const fetchSizesByItemTypeIdAction = createAsyncThunk(
  'attributeValues/fetchSizesByItemTypeId',
  async (itemTypeId: any) => {
    return await getSizesByItemTypeId(itemTypeId)
  },
)

export const fetchMerchStylesAction = createAsyncThunk(
  'attributeValues/fetchMerchStyles',
  async (deptClazzSubclazz: any) => {
    return await getMerchStyles(deptClazzSubclazz)
  },
)

export const fetchVMMAttributes = createAsyncThunk(
  'attributeValues/fetchVMMAttributes',
  async (data: { eventId; bidId; vendors }) => {
    const { eventId, bidId, vendors } = data
    return await getVMMAttributes(eventId, bidId, vendors)
  },
)

type FetchApexIdsParams = {
  eventId
  bpId
}

export const fetchDynamicMasterApexIds = createAsyncThunk<
  any,
  FetchApexIdsParams,
  { state: RootState }
>(
  'attributeValues/fetchDynamicMasterApexIds',
  async (
    data: FetchApexIdsParams,
    { getState }: { getState: () => RootState },
  ) => {
    const { eventId, bpId } = data

    const bpIds = bpId
      ? bpId
      : getState().vendors.bpIds.length > 0
        ? getState().vendors.bpIds
        : [getState().auth.partnerId]

    return await getDynamicMasterApexIds(eventId, bpIds)
  },
)

export const fetchStandardApexIds = createAsyncThunk<
  any,
  FetchApexIdsParams,
  { state: RootState }
>(
  'attributeValues/fetchStandardApexIds',
  async (
    data: FetchApexIdsParams,
    { getState }: { getState: () => RootState },
  ) => {
    const { eventId, bpId } = data

    const bpIds = bpId
      ? bpId
      : getState().vendors.bpIds.length > 0
        ? getState().vendors.bpIds
        : [getState().auth.partnerId]

    return await getStandardApexIds(eventId, bpIds)
  },
)

export const fetchAutocompleteAttributeValuesAction = createAsyncThunk(
  'attributeValues/fetchAutocompleteAttributeValues',
  async (data: { searchText; attributeType; parentFamilyId }) => {
    const { searchText, attributeType, parentFamilyId } = data

    return await getAutocompleteAttributeValues(
      searchText,
      attributeType,
      parentFamilyId,
    )
  },
)

export const fetchAllDeptStandardReturnPoliciesAction = createAsyncThunk(
  'attributeValues/fetchAllDeptStandardReturnPolicies',
  async () => {
    return await getDeptStandardReturnPolicies()
  },
)

export const fetchPidsAction = createAsyncThunk(
  'attributeValues/fetchPids',
  async (data: { bpIds; deptIds }) => {
    const { bpIds, deptIds } = data

    return await getPids(bpIds, deptIds)
  },
)

export const fetchPidsDetailsAction = createAsyncThunk(
  'attributeValues/fetchPidsDetails',
  async (pidIds: any) => {
    return await getPidsDetails(pidIds)
  },
)

export const fetchColorFamiliesAction = createAsyncThunk(
  'attributeValues/fetchColorFamilies',
  async () => {
    return await getColorFamilies()
  },
)

export const fetchPatternFamiliesAction = createAsyncThunk(
  'attributeValues/fetchPatternFamilies',
  async () => {
    return await getPatternFamilies()
  },
)

export const fetchVendorsByBpIdsAction = createAsyncThunk(
  'attributeValues/fetchVendorsByBpIds',
  async (bpIds: any) => {
    return await getVendorsByBpIds(bpIds)
  },
)

const initialState = {
  bpId_dynamicMasterApexID: {},
  dept_classes: {},
  bpIdPids_dept: {},
  deptClass_subclasses: {},
  deptClassSubclass_merchStyles: {},
  dept_merchTypes: {},
  merchTypeId_itemTypes: {},
  itemTypeId_sizes: {},
  allCountries: [],
  country_portOfExports: {},
  portOfExport_factoryCities: {},
  vendorId_vendorAttributes: {},
  autocompleteAtrributeValues: {},
  deptId_stdRetPolicies: {},
  colorFamilies: [],
  patternFamilies: [],
  vendorsByBpId: {},
  pidIds_Details: {},
  fetchCounter: 0,
  completionsCounter: 0,
  isAttributeValuesLoaded: false,
  bpId_standardApexID: {},
}

const incrementFetches = state => {
  state.fetchCounter = state.fetchCounter + 1
  state.isAttributeValuesLoaded = false
}

const incrementCompletions = state => {
  const completionsCounter = state.completionsCounter + 1
  state.completionsCounter = completionsCounter
  state.isAttributeValuesLoaded = state.fetchCounter === completionsCounter
}

const incrementActions = [
  fetchClazzAction,
  fetchSubClazzAction,
  fetchPortOfExportsAndFactoryCitiesAction,
  fetchAllCountriesAction,
  fetchMerchTypesAction,
  fetchItemTypesAction,
  fetchSizesByItemTypeIdAction,
  fetchMerchStylesAction,
  fetchVMMAttributes,
  fetchDynamicMasterApexIds,
  fetchStandardApexIds,
  fetchAutocompleteAttributeValuesAction,
  fetchAllDeptStandardReturnPoliciesAction,
  fetchColorFamiliesAction,
  fetchPidsAction,
  fetchPidsDetailsAction,
  fetchPatternFamiliesAction,
]

const attributeValuesSlice = createSlice({
  name: 'attributeValues',
  initialState,
  reducers: {
    skipAttributeLoadingAction: state => {
      state.isAttributeValuesLoaded = true
    },
  },
  extraReducers: builder => {
    incrementActions.forEach(action => {
      builder.addCase(action.pending, state => {
        incrementFetches(state)
      })
      builder.addCase(action.rejected, state => {
        incrementCompletions(state)
      })
    })

    // fetchClazzAction
    builder.addCase(fetchClazzAction.fulfilled, (state, action) => {
      const { payload } = action
      const dept_classes_temp = {
        ...state.dept_classes,
        ...payload?.data,
      }

      state.dept_classes = dept_classes_temp
      incrementCompletions(state)
    })

    // fetchSubClazzAction
    builder.addCase(fetchSubClazzAction.fulfilled, (state, action) => {
      const { payload } = action
      const deptClass_subclasses_temp = {
        ...state.deptClass_subclasses,
        ...payload?.data,
      }

      state.deptClass_subclasses = deptClass_subclasses_temp
      incrementCompletions(state)
    })

    // fetchPortOfExportsAndFactoryCitiesAction
    builder.addCase(
      fetchPortOfExportsAndFactoryCitiesAction.fulfilled,
      (state, action) => {
        const { payload } = action
        const country_portOfExports_temp = {
          ...state.country_portOfExports,
          ...payload?.data.portOfExports,
        }
        const portOfExport_factoryCities_temp = {
          ...state.portOfExport_factoryCities,
          ...payload?.data.factoryCities,
        }

        state.country_portOfExports = country_portOfExports_temp
        state.portOfExport_factoryCities = portOfExport_factoryCities_temp
        incrementCompletions(state)
      },
    )

    // fetchAllCountriesAction
    builder.addCase(fetchAllCountriesAction.fulfilled, (state, action) => {
      const { payload } = action

      state.allCountries = payload?.data
      incrementCompletions(state)
    })

    // fetchMerchTypesAction
    builder.addCase(fetchMerchTypesAction.fulfilled, (state, action) => {
      const { payload } = action
      const dept_merchTypes_temp = {
        ...state.dept_merchTypes,
        ...payload?.data.merchTypes,
      }

      state.dept_merchTypes = dept_merchTypes_temp
      incrementCompletions(state)
    })

    // fetchItemTypesAction
    builder.addCase(fetchItemTypesAction.fulfilled, (state, action) => {
      const { payload } = action
      const merchTypeId_itemTypes_temp = {
        ...state.merchTypeId_itemTypes,
        ...payload?.data,
      }

      state.merchTypeId_itemTypes = merchTypeId_itemTypes_temp
      incrementCompletions(state)
    })

    // fetchSizesByItemTypeIdAction
    builder.addCase(fetchSizesByItemTypeIdAction.fulfilled, (state, action) => {
      const { payload } = action
      const itemTypeId_sizes_temp = {
        ...state.itemTypeId_sizes,
        ...payload?.data,
      }

      state.itemTypeId_sizes = itemTypeId_sizes_temp
      incrementCompletions(state)
    })

    // fetchMerchStylesAction
    builder.addCase(fetchMerchStylesAction.fulfilled, (state, action) => {
      const { payload } = action
      const deptClassSubclass_merchStyles_temp = {
        ...state.deptClassSubclass_merchStyles,
        ...payload?.data,
      }

      state.deptClassSubclass_merchStyles = deptClassSubclass_merchStyles_temp
      incrementCompletions(state)
    })

    // fetchVMMAttributes
    builder.addCase(fetchVMMAttributes.fulfilled, (state, action) => {
      const { payload } = action
      const vendorAttributes_temp = {
        ...state.vendorId_vendorAttributes,
        ...payload?.data,
      }

      state.vendorId_vendorAttributes = vendorAttributes_temp
      incrementCompletions(state)
    })

    // fetchDynamicMasterApexIds
    builder.addCase(fetchDynamicMasterApexIds.fulfilled, (state, action) => {
      const { payload } = action
      const dynamicMasterApexID_temp = {
        ...state.bpId_dynamicMasterApexID,
        ...getDynamicMasterMap(
          payload?.data.master_grouping_attributes_response,
        ),
      }

      state.bpId_dynamicMasterApexID = dynamicMasterApexID_temp
      incrementCompletions(state)
    })

    // fetchStandardApexIds
    builder.addCase(fetchStandardApexIds.fulfilled, (state, action) => {
      const { payload } = action
      const standardApexID_temp = getDynamicMasterMap(
        payload?.data.master_grouping_attributes_response,
      )

      state.bpId_standardApexID = standardApexID_temp
      incrementCompletions(state)
    })

    // fetchAutocompleteAttributeValuesAction
    builder.addCase(
      fetchAutocompleteAttributeValuesAction.fulfilled,
      (state, action) => {
        const { payload } = action
        const { attributeType, searchText, parentFamilyId } = action.meta.arg
        const transformedData = payload?.data.map(value => {
          return { label: value.name, value: value.fieldId }
        })
        let autocompleteAtrributeValues_temp
        if (searchText !== null) {
          autocompleteAtrributeValues_temp = {
            ...state.autocompleteAtrributeValues,
            [attributeType!]: {
              ...state.autocompleteAtrributeValues[attributeType!],
              [searchText]: transformedData,
            },
          }
        }
        if (parentFamilyId !== null) {
          autocompleteAtrributeValues_temp = {
            ...state.autocompleteAtrributeValues,
            [attributeType!]: {
              ...state.autocompleteAtrributeValues[attributeType!],
              [parentFamilyId]: transformedData,
            },
          }
        }

        state.autocompleteAtrributeValues = autocompleteAtrributeValues_temp
        incrementCompletions(state)
      },
    )

    // fetchAllDeptStandardReturnPoliciesAction
    builder.addCase(
      fetchAllDeptStandardReturnPoliciesAction.fulfilled,
      (state, action) => {
        state.deptId_stdRetPolicies = action.payload?.data
        incrementCompletions(state)
      },
    )

    // fetchPidsAction
    builder.addCase(fetchPidsAction.fulfilled, (state, action) => {
      const bpIdPidsDept = {
        bpsPids: action.payload.data,
        deptIds: action.meta.arg.deptIds,
      }

      state.bpIdPids_dept = bpIdPidsDept
      incrementCompletions(state)
    })

    // fetchPidsDetailsAction
    builder.addCase(fetchPidsDetailsAction.fulfilled, (state, action) => {
      const { payload } = action
      const pidIds_Details = payload?.data?.pidDetails?.reduce(
        (pidMap, pid) => {
          pidMap[pid.productId] = pid
          return pidMap
        },
        {},
      )

      state.pidIds_Details = pidIds_Details
      incrementCompletions(state)
    })

    // fetchColorFamiliesAction
    builder.addCase(fetchColorFamiliesAction.fulfilled, (state, action) => {
      state.colorFamilies = action.payload?.data
      incrementCompletions(state)
    })

    // fetchPatternFamiliesAction
    builder.addCase(fetchPatternFamiliesAction.fulfilled, (state, action) => {
      state.patternFamilies = action.payload?.data
      incrementCompletions(state)
    })

    // fetchVendorsByBpIdsAction
    builder.addCase(fetchVendorsByBpIdsAction.pending, state => {
      state.vendorsByBpId = {}
      incrementFetches(state)
    })
    builder.addCase(fetchVendorsByBpIdsAction.fulfilled, (state, action) => {
      const vendorsByBpTemp: any = {}
      const vendorBpList: any[] = []
      const importVendors: any[] = []
      const domVendors: any[] = []
      const dvsVendors: any[] = []
      ;(action.payload.data as any).vendors.forEach(vendor => {
        const vendorBpMap: any = {}
        vendorBpMap.bpId = vendor.bpId
        vendorBpMap.vendorNumber = vendor.vendorNumber
        vendorBpList.push(vendorBpMap)
        switch (vendor.vendorType) {
          case 'DOMESTIC':
            domVendors.push(vendor.vendorNumber)
            break
          case 'IMPORT':
            importVendors.push(vendor.vendorNumber)
            break
          case 'DVS':
            dvsVendors.push(vendor.vendorNumber)
            break
          default:
            break
        }
      })
      vendorsByBpTemp.impVendorIds = importVendors
      vendorsByBpTemp.domVendorIds = domVendors
      vendorsByBpTemp.dvsVendorIds = dvsVendors
      vendorsByBpTemp.vendorBpList = vendorBpList

      state.vendorsByBpId = vendorsByBpTemp
      incrementCompletions(state)
    })
    builder.addCase(fetchVendorsByBpIdsAction.rejected, state => {
      incrementCompletions(state)
    })
  },
})

export const { skipAttributeLoadingAction } = attributeValuesSlice.actions

export default attributeValuesSlice.reducer
